import React, { useState, useEffect } from 'react';
import { Plus, CheckCircle, AlertCircle, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { userService } from '../../../services/firestoreService';
import { departmentService } from '../../../services/firebaseService';
import authService from '../../../services/authService';
import { useNotifications } from '../../NotificationSystem';
import './SetupWizardSteps.css';

const Step3Users = ({ stepData, onComplete, onValidation, userData, structure }) => {
  const { notify } = useNotifications();
  const { t } = useTranslation('setup');
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [creatingUser, setCreatingUser] = useState(false);

  // Load existing users and departments
  useEffect(() => {
    const loadData = async () => {
      try {
        const [usersData, departmentsData] = await Promise.all([
          userService.getAll(),
          departmentService.getAll()
        ]);
        setUsers(usersData || []);
        setDepartments(departmentsData || []);
        checkMinimumRequirements(usersData || []);
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };
    loadData();
  }, []);

  const checkMinimumRequirements = (usersList) => {
    const requirements = {
      management: usersList.filter(u => u.role_id === 'management').length >= 1,
      icPersonnel: usersList.filter(u => u.role_id === 'ic_personnel').length >= 1,
      administration: usersList.filter(u => u.role_id === 'administration').length >= 1
    };

    const allMet = Object.values(requirements).every(met => met);
    
    if (allMet) {
      onComplete({
        management: usersList.filter(u => u.role_id === 'management').length,
        icPersonnel: usersList.filter(u => u.role_id === 'ic_personnel').length,
        administration: usersList.filter(u => u.role_id === 'administration').length,
        requirementsMet: true
      });
    }
  };

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role_id: '',
    primary_department_id: '',
    phone_primary: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.first_name.trim()) {
      newErrors.first_name = t('step3.errors.firstNameRequired');
    }
    
    if (!formData.last_name.trim()) {
      newErrors.last_name = t('step3.errors.lastNameRequired');
    }
    
    if (!formData.email.trim()) {
      newErrors.email = t('step3.errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('step3.errors.emailInvalid');
    }
    
    if (!formData.password) {
      newErrors.password = t('step3.errors.passwordRequired');
    } else if (formData.password.length < 6) {
      newErrors.password = t('step3.errors.passwordTooShort');
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('step3.errors.passwordMismatch');
    }
    
    if (!formData.role_id) {
      newErrors.role_id = t('step3.errors.roleRequired');
    }
    
    if (!formData.primary_department_id) {
      newErrors.primary_department_id = t('step3.errors.departmentRequired');
    }
    
    if (!formData.phone_primary.trim()) {
      newErrors.phone_primary = t('step3.errors.phoneRequired');
    }
    
    setErrors(newErrors);
    onValidation(Object.keys(newErrors).length > 0 ? Object.values(newErrors) : null);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    setCreatingUser(true);
    
    try {
      const userData = {
        badge_id: `BADGE-${Date.now()}`,
        first_name: formData.first_name,
        last_name: formData.last_name,
        employee_id: `EMP-${Date.now()}`,
        phone_primary: formData.phone_primary,
        email: formData.email,
        password: formData.password,
        role_id: formData.role_id,
        primary_department_id: formData.primary_department_id,
        status: 'active',
        initials: `${formData.first_name.charAt(0)}${formData.last_name.charAt(0)}`.toUpperCase(),
        full_name: `${formData.first_name} ${formData.last_name}`
      };

      const result = await authService.createUser(userData);
      
      // Reload users
      const usersData = await userService.getAll();
      setUsers(usersData || []);
      checkMinimumRequirements(usersData || []);
      
      // Reset form
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        confirmPassword: '',
        role_id: '',
        primary_department_id: '',
        phone_primary: ''
      });
      
      notify.success(t('step3.notifications.createdSuccess', { name: userData.full_name }), t('step3.notifications.createdSuccessTitle'));
    } catch (error) {
      console.error('Error creating user:', error);
      notify.error(error.message || t('step3.notifications.createFailed'), t('step3.notifications.createFailedTitle'));
      setErrors({ general: error.message || 'Failed to create user' });
    } finally {
      setCreatingUser(false);
    }
  };

  const getUsersByRole = (roleId) => {
    return users.filter(u => u.role_id === roleId);
  };

  const requirements = {
    management: getUsersByRole('management').length >= 1,
    icPersonnel: getUsersByRole('ic_personnel').length >= 1,
    administration: getUsersByRole('administration').length >= 1
  };

  const allRequirementsMet = Object.values(requirements).every(met => met);

  const getRoleLabel = (roleId) => {
    const roleKeys = {
      'management': 'step3.roleOptions.management',
      'ic_personnel': 'step3.roleOptions.icPersonnel',
      'administration': 'step3.roleOptions.administration',
      'execution': 'step3.roleOptions.execution',
      'prescriptors': 'step3.roleOptions.prescriptors'
    };
    return roleKeys[roleId] ? t(roleKeys[roleId]) : roleId;
  };

  return (
    <div className="step-content">
      <div className="step-section">
        <h3>{t('step3.title')}</h3>
        <p>{t('step3.description1')}</p>
        <p>
          <strong>{t('step3.noteLabel')}</strong> {t('step3.noteText')}
        </p>
      </div>

      {departments.length === 0 && (
        <div className="error-message" style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#fff3cd', color: '#856404', borderRadius: '6px' }}>
          {t('step3.completeStep2Warning')}
        </div>
      )}

      {errors.general && (
        <div className="error-message" style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#fee', color: '#c33', borderRadius: '6px' }}>
          {errors.general}
        </div>
      )}

      <div className="requirements-section" style={{ marginBottom: '32px' }}>
        <h3>{t('step3.requiredUsersTitle')}</h3>
        <div className="requirements-list">
          <div className={`requirement-item ${requirements.management ? 'met' : 'pending'}`}>
            {requirements.management ? <CheckCircle size={20} color="#4caf50" /> : <AlertCircle size={20} color="#ff9800" />}
            <span>{t('step3.requirements.management', { count: getUsersByRole('management').length })}</span>
            {getUsersByRole('management').length > 0 && (
              <span style={{ marginLeft: '8px', color: '#666', fontSize: '12px' }}>
                ({getUsersByRole('management').map(u => u.full_name).join(', ')})
              </span>
            )}
          </div>
          <div className={`requirement-item ${requirements.icPersonnel ? 'met' : 'pending'}`}>
            {requirements.icPersonnel ? <CheckCircle size={20} color="#4caf50" /> : <AlertCircle size={20} color="#ff9800" />}
            <span>{t('step3.requirements.icPersonnel', { count: getUsersByRole('ic_personnel').length })}</span>
            {getUsersByRole('ic_personnel').length > 0 && (
              <span style={{ marginLeft: '8px', color: '#666', fontSize: '12px' }}>
                ({getUsersByRole('ic_personnel').map(u => u.full_name).join(', ')})
              </span>
            )}
          </div>
          <div className={`requirement-item ${requirements.administration ? 'met' : 'pending'}`}>
            {requirements.administration ? <CheckCircle size={20} color="#4caf50" /> : <AlertCircle size={20} color="#ff9800" />}
            <span>{t('step3.requirements.administration', { count: getUsersByRole('administration').length })}</span>
            {getUsersByRole('administration').length > 0 && (
              <span style={{ marginLeft: '8px', color: '#666', fontSize: '12px' }}>
                ({getUsersByRole('administration').map(u => u.full_name).join(', ')})
              </span>
            )}
          </div>
        </div>

        {allRequirementsMet && (
          <div className="success-message" style={{ marginTop: '20px' }}>
            {t('step3.allRequirementsMet')}
          </div>
        )}
      </div>

      <div className="user-creation-section">
        <h3>{t('step3.createUserSectionTitle')}</h3>
        <form onSubmit={handleCreateUser}>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="first_name">
                {t('step3.fields.firstName')} <span className="required">*</span>
              </label>
              <input
                type="text"
                id="first_name"
                value={formData.first_name}
                onChange={(e) => handleChange('first_name', e.target.value)}
                className={errors.first_name ? 'error' : ''}
                required
              />
              {errors.first_name && <span className="error-text">{errors.first_name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="last_name">
                {t('step3.fields.lastName')} <span className="required">*</span>
              </label>
              <input
                type="text"
                id="last_name"
                value={formData.last_name}
                onChange={(e) => handleChange('last_name', e.target.value)}
                className={errors.last_name ? 'error' : ''}
                required
              />
              {errors.last_name && <span className="error-text">{errors.last_name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="email">
                {t('step3.fields.email')} <span className="required">*</span>
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className={errors.email ? 'error' : ''}
                required
              />
              {errors.email && <span className="error-text">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="phone_primary">
                {t('step3.fields.phone')} <span className="required">*</span>
              </label>
              <input
                type="tel"
                id="phone_primary"
                value={formData.phone_primary}
                onChange={(e) => handleChange('phone_primary', e.target.value)}
                className={errors.phone_primary ? 'error' : ''}
                required
              />
              {errors.phone_primary && <span className="error-text">{errors.phone_primary}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="role_id">
                {t('step3.fields.role')} <span className="required">*</span>
              </label>
              <select
                id="role_id"
                value={formData.role_id}
                onChange={(e) => handleChange('role_id', e.target.value)}
                className={errors.role_id ? 'error' : ''}
                required
              >
                <option value="">{t('step3.roleOptions.selectRole')}</option>
                <option value="management">{t('step3.roleOptions.management')}</option>
                <option value="ic_personnel">{t('step3.roleOptions.icPersonnel')}</option>
                <option value="administration">{t('step3.roleOptions.administration')}</option>
                <option value="execution">{t('step3.roleOptions.execution')}</option>
                <option value="prescriptors">{t('step3.roleOptions.prescriptors')}</option>
              </select>
              {errors.role_id && <span className="error-text">{errors.role_id}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="primary_department_id">
                {t('step3.fields.department')} <span className="required">*</span>
              </label>
              <select
                id="primary_department_id"
                value={formData.primary_department_id}
                onChange={(e) => handleChange('primary_department_id', e.target.value)}
                className={errors.primary_department_id ? 'error' : ''}
                disabled={departments.length === 0}
                required
              >
                <option value="">
                  {departments.length === 0 ? t('step3.noDepartments') : t('step3.selectDepartment')}
                </option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
              {errors.primary_department_id && <span className="error-text">{errors.primary_department_id}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="password">
                {t('step3.fields.password')} <span className="required">*</span>
              </label>
              <input
                type="password"
                id="password"
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                className={errors.password ? 'error' : ''}
                placeholder={t('step3.fields.passwordPlaceholder')}
                required
              />
              {errors.password && <span className="error-text">{errors.password}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">
                {t('step3.fields.confirmPassword')} <span className="required">*</span>
              </label>
              <input
                type="password"
                id="confirmPassword"
                value={formData.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                className={errors.confirmPassword ? 'error' : ''}
                required
              />
              {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
            </div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={creatingUser || departments.length === 0}
            >
              {creatingUser ? t('step3.creating') : t('step3.createUser')}
            </button>
          </div>
        </form>
      </div>

      {users.length > 0 && (
        <div className="users-list-section" style={{ marginTop: '32px' }}>
          <h3>{t('step3.createdUsersListTitle', { count: users.length })}</h3>
          <div className="users-list">
            {users.map(user => (
              <div key={user.id} className="user-item" style={{ padding: '12px', border: '1px solid #ddd', borderRadius: '6px', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <User size={20} />
                  <div>
                    <strong>{user.full_name}</strong>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {getRoleLabel(user.role_id)} • {user.email}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Step3Users;
